import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { CreateMonitorButton } from '@/components/create-monitor-block'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { apiV2 } from '@/lib/api-v2-client'
import ReactECharts from 'echarts-for-react'
import { CHART_FONT } from '@/lib/chart-font'
import { uptimeAxis } from '@/lib/chart-axis'
import { CatPeeking } from '@/components/mascot'
import { OnboardingChecklist } from '@/components/onboarding-checklist'
import { StatusBadge } from '@/components/status-badge'
import { BrowserUsageMeter } from '@/components/browser-usage-meter'
import { TriangleAlert } from 'lucide-react'
import { useTour } from '@/hooks/use-tour'
import { useCreateMonitorBlock } from '@/hooks/use-create-monitor-block'
import { NO_DATA_LABEL, uptimeText } from '@/lib/uptime-display'

export const Route = createFileRoute('/_authenticated/dashboard/')({
  component: DashboardPage,
})

const dashboardTourSteps = [
  {
    element: '#tour-add-monitor',
    popover: {
      title: 'Add a Monitor',
      description: 'Start by adding your first website or API endpoint to monitor. We check it every 30 seconds.',
    },
  },
  {
    element: '#tour-stats',
    popover: {
      title: 'Your Stats at a Glance',
      description: 'See overall uptime, active monitors, and ongoing incidents right here.',
    },
  },
  {
    element: '#tour-monitors-table',
    popover: {
      title: 'Monitor List',
      description: 'All your monitors appear here with live status, response time, and uptime percentage.',
    },
  },
  {
    element: '#tour-onboarding',
    popover: {
      title: 'Getting Started Checklist',
      description: 'Follow these steps to get fully set up: create a monitor, add alerts, and build a status page.',
    },
  },
]

function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiV2.getDashboardStats(),
    refetchInterval: 30_000,
  })

  const { data: monitors, isLoading: monitorsLoading } = useQuery({
    queryKey: ['monitors'],
    queryFn: () => apiV2.listMonitors({ per_page: 1000 }),
    refetchInterval: 30_000,
  })

  const { data: analytics } = useQuery({
    queryKey: ['dashboard-analytics'],
    queryFn: () => apiV2.getDashboardAnalytics(30),
  })

  const { data: trends } = useQuery({
    queryKey: ['organization-trends'],
    queryFn: () => apiV2.getOrganizationTrends(30),
  })

  const { data: alertChannels } = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => apiV2.listAlertChannels(),
  })

  const { data: statusPages } = useQuery({
    queryKey: ['status-pages'],
    queryFn: () => apiV2.listStatusPages(),
  })

  const hasAlertChannels = (alertChannels?.data?.length ?? 0) > 0
  const isLoading = statsLoading || monitorsLoading
  // The server sends 100.0 overall uptime when there are no monitors. No monitors means no data.
  const trendAxis = uptimeAxis((analytics?.uptime_trend ?? []).map((d: { uptime: number }) => d.uptime))
  const hasUptimeData = !!analytics && (monitors?.data?.length ?? 0) > 0
  const createBlock = useCreateMonitorBlock()

  useTour({
    name: 'dashboard',
    steps: dashboardTourSteps,
    // Its first step points at Add Monitor, so it waits until that button works.
    when: !isLoading && !createBlock,
  })

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor your services and track uptime
          </p>
        </div>
        <CreateMonitorButton id="tour-add-monitor" className="w-full sm:w-auto">Add Monitor</CreateMonitorButton>
      </div>

      {/* Warning: monitors exist but no alert channels */}
      {!isLoading && (monitors?.data?.length ?? 0) > 0 && !hasAlertChannels && (
        <Alert className="border-status-degraded bg-status-degraded-soft">
          <TriangleAlert className="text-status-degraded" />
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-status-degraded">
              You have monitors running but no alert channels configured. You won't be notified when your sites go down.
            </span>
            <Button size="sm" variant="outline" asChild className="shrink-0 border-status-degraded text-status-degraded hover:bg-status-degraded-soft">
              <Link to="/dashboard/alerts">Set up alerts</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Onboarding Checklist */}
      <div id="tour-onboarding">
        <OnboardingChecklist
          monitorCount={monitors?.data?.length ?? 0}
          alertChannelCount={alertChannels?.data?.length ?? 0}
          statusPageCount={statusPages?.data?.length ?? 0}
        />
      </div>

      {/* Stats Cards */}
      <div id="tour-stats" className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Overall Uptime"
          value={uptimeText(analytics?.overall_uptime, hasUptimeData)}
          valueLabel={hasUptimeData ? undefined : NO_DATA_LABEL}
          isLoading={isLoading}
          className={
            analytics && hasUptimeData
              ? analytics.overall_uptime >= 99.9
                ? 'text-status-up'
                : analytics.overall_uptime >= 95
                  ? 'text-status-degraded'
                  : 'text-status-down'
              : undefined
          }
        />
        <StatsCard
          title="Active Monitors"
          value={stats?.data?.active_monitors ?? 0}
          isLoading={isLoading}
        />
        <StatsCard
          title="Ongoing Incidents"
          value={stats?.data?.ongoing_incidents ?? 0}
          isLoading={isLoading}
          className={stats?.data?.ongoing_incidents ? 'text-status-down' : undefined}
        />
        <StatsCard
          title="Recent Incidents"
          value={stats?.data?.recent_incidents ?? 0}
          isLoading={isLoading}
        />
      </div>

      {/* Browser-check usage — only for orgs that run browser monitors. */}
      {(monitors?.data?.some((m) => m.monitor_type === 'browser') ?? false) && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <BrowserUsageMeter hasBrowserMonitor />
        </div>
      )}

      {/* Uptime Trend Chart */}
      {analytics && analytics.uptime_trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Uptime Trend</CardTitle>
            <CardDescription>
              Organization-wide uptime over the last 30 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ReactECharts
              style={{ height: 250 }}
              option={{
                textStyle: { fontFamily: CHART_FONT },
                grid: { top: 10, right: 16, bottom: 24, left: 50 },
                tooltip: {
                  trigger: 'axis',
                  formatter: (params: Array<{ value: number; axisValueLabel: string }>) => {
                    const date = new Date(params[0].axisValueLabel).toLocaleDateString()
                    return `${date}<br/>Uptime: ${params[0].value.toFixed(2)}%`
                  },
                },
                xAxis: {
                  type: 'category',
                  data: analytics.uptime_trend.map((d: { date: string }) => d.date),
                  axisLabel: {
                    formatter: (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    fontSize: 11,
                  },
                  axisTick: { show: false },
                },
                yAxis: {
                  type: 'value',
                  min: trendAxis.min,
                  max: trendAxis.max,
                  axisLabel: { formatter: (v: number) => `${v}%`, fontSize: 11 },
                  splitLine: { lineStyle: { type: 'dashed', opacity: 0.3 } },
                },
                series: [{
                  type: 'line',
                  data: analytics.uptime_trend.map((d: { uptime: number }) => d.uptime),
                  smooth: true,
                  showSymbol: false,
                  lineStyle: { width: 2 },
                  areaStyle: { opacity: 0.1 },
                  itemStyle: { color: 'hsl(142.1, 76.2%, 36.3%)' },
                }],
              }}
            />
            {trendAxis.note && <p className="text-sm text-muted-foreground">{trendAxis.note}</p>}
          </CardContent>
        </Card>
      )}

      {/* Incident Frequency + Top Offenders */}
      {trends && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Incident Frequency Chart */}
          {trends.incident_frequency.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Incident Frequency</CardTitle>
                <CardDescription>Daily incidents over the last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <ReactECharts
                  style={{ height: 200 }}
                  option={{
                    textStyle: { fontFamily: CHART_FONT },
                    grid: { top: 10, right: 16, bottom: 24, left: 30 },
                    tooltip: {
                      trigger: 'axis',
                      formatter: (params: Array<{ value: number; axisValueLabel: string }>) => {
                        const date = new Date(params[0].axisValueLabel).toLocaleDateString()
                        return `${date}<br/>Incidents: ${params[0].value}`
                      },
                    },
                    xAxis: {
                      type: 'category',
                      data: trends.incident_frequency.map((d: { date: string }) => d.date),
                      axisLabel: {
                        formatter: (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        fontSize: 11,
                      },
                      axisTick: { show: false },
                    },
                    yAxis: {
                      type: 'value',
                      minInterval: 1,
                      axisLabel: { fontSize: 11 },
                      splitLine: { lineStyle: { type: 'dashed', opacity: 0.3 } },
                    },
                    series: [{
                      type: 'bar',
                      data: trends.incident_frequency.map((d: { count: number }) => d.count),
                      itemStyle: { color: 'hsl(0, 84.2%, 60.2%)', borderRadius: [2, 2, 0, 0] },
                    }],
                  }}
                />
              </CardContent>
            </Card>
          )}

          {/* Top Offenders */}
          {trends.top_offenders.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Most Incidents</CardTitle>
                <CardDescription>Monitors with the most incidents (30 days)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trends.top_offenders.map((offender) => (
                    <div key={offender.monitor_id} className="flex items-center justify-between">
                      <div>
                        <Link
                          to="/dashboard/monitors/$id"
                          params={{ id: String(offender.monitor_id) }}
                          className="font-medium hover:underline"
                        >
                          {offender.monitor_name}
                        </Link>
                        <p className="text-sm text-muted-foreground">
                          {formatDowntime(offender.total_downtime_seconds)} total downtime
                        </p>
                      </div>
                      <Badge variant="destructive">{offender.incident_count} incidents</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Monitors Table */}
      <Card id="tour-monitors-table">
        <CardHeader>
          <CardTitle>Your Monitors</CardTitle>
          <CardDescription>
            Overview of all your monitored services
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : monitors?.data && monitors.data.length > 0 ? (
            <div className="space-y-4">
              {monitors.data.map((monitor) => (
                <div
                  key={monitor.id}
                  className="flex flex-col gap-2 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/dashboard/monitors/$id"
                        params={{ id: String(monitor.id) }}
                        className="truncate font-medium hover:underline"
                      >
                        {monitor.name}
                      </Link>
                      <StatusBadge status={monitor.last_check?.status as 'up' | 'down' | 'unknown' | undefined} />
                      {!hasAlertChannels && (
                        <Link to="/dashboard/alerts">
                          <Badge
                            variant="outline"
                            className="border-status-degraded text-status-degraded hover:bg-status-degraded-soft"
                            title="No alert channels configured — click to add one"
                          >
                            No alerts
                          </Badge>
                        </Link>
                      )}
                    </div>
                    <p className="truncate text-sm text-muted-foreground">{monitor.url}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {monitor.last_check?.response_time && (
                      <span>{monitor.last_check.response_time}ms</span>
                    )}
                    {monitor.uptime_percentage != null && (
                      <span>{monitor.uptime_percentage.toFixed(1)}% uptime</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <CatPeeking size={120} className="mx-auto mb-4" />
              <p className="mb-4 text-muted-foreground">No monitors yet</p>
              <CreateMonitorButton>Add Your First Monitor</CreateMonitorButton>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatsCard({
  title,
  value,
  valueLabel,
  isLoading,
  className,
}: {
  title: string
  value: string | number
  /** Spoken label for a value that is only a mark, such as "--". */
  valueLabel?: string
  isLoading: boolean
  className?: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
      </CardHeader>
      <CardContent aria-busy={isLoading}>
        {isLoading ? (
          <div className="h-8 w-20 animate-pulse rounded bg-muted" aria-hidden="true" />
        ) : (
          <p className={`text-2xl font-semibold ${className ?? ''}`}>
            {valueLabel ? <span role="img" aria-label={valueLabel}>{value}</span> : value}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function formatDowntime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}
